import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { ShieldCheck, Printer, ArrowLeft, Download } from "lucide-react";

export default function OwnershipCredential() {
    const { id } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: "" });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/credentials/${id}`);
                setState({ loading: false, data, error: "" });
            } catch (e) {
                setState({ loading: false, data: null, error: formatApiErrorDetail(e?.response?.data?.detail) || "Could not load credential" });
            }
        })();
    }, [id]);

    if (state.loading) return (
        <div className="min-h-screen bg-ink-300"><Navbar /><div className="p-16 text-silver-500">Loading credential…</div></div>
    );
    if (state.error) return (
        <div className="min-h-screen bg-ink-300"><Navbar /><div className="p-16 text-red-300">{state.error}</div></div>
    );

    const d = state.data;
    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <div className="print:hidden">
                <Navbar />
            </div>
            <section className="max-w-[980px] mx-auto px-4 md:px-10 pt-10 pb-20">
                <div className="flex items-center justify-between mb-6 print:hidden">
                    <Link to={`/vault/${id}`} className="inline-flex items-center gap-2 text-silver-400 text-sm hover:text-platinum">
                        <ArrowLeft className="w-4 h-4" /> Back to Vault
                    </Link>
                    <button
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-2 bg-platinum hover:bg-white text-ink-400 px-4 py-2.5 text-xs tracking-widest uppercase rounded-sm"
                        data-testid="print-credential-btn"
                    >
                        <Printer className="w-4 h-4" /> Print
                    </button>
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/credentials/${id}/pdf`}
                        className="inline-flex items-center gap-2 border border-platinum text-platinum hover:bg-platinum/10 px-4 py-2.5 text-xs tracking-widest uppercase rounded-sm"
                        data-testid="download-pdf-btn"
                        onClick={async (e) => {
                            // Use fetch with auth header to stream the PDF and trigger download.
                            e.preventDefault();
                            const token = localStorage.getItem("sr_token");
                            const resp = await fetch(e.currentTarget.href, {
                                headers: token ? { Authorization: `Bearer ${token}` } : {},
                            });
                            if (!resp.ok) return;
                            const blob = await resp.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${d.credential_id}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                        }}
                    >
                        <Download className="w-4 h-4" /> Download PDF
                    </a>
                </div>

                <div
                    id="credential-sheet"
                    className="bg-white text-ink-400 rounded-sm border border-silver-400 shadow-[0_40px_120px_rgba(0,0,0,0.6)] p-10 md:p-14 relative overflow-hidden"
                    data-testid="credential-sheet"
                >
                    {/* watermark S */}
                    <div aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04] select-none">
                        <span className="font-display text-[22rem] leading-none">S</span>
                    </div>

                    <div className="relative">
                        <div className="flex items-start justify-between border-b border-ink-400/20 pb-6 mb-8">
                            <div>
                                <div className="text-[11px] tracking-[0.3em] uppercase text-ink-400/60 mb-2">Silver Screen Xchange</div>
                                <div className="font-display text-3xl md:text-4xl leading-none">Certificate of Exclusive<br/>Copyright Ownership</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-400/60 mb-1">Credential No.</div>
                                <div className="font-mono text-base">{d.credential_id}</div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-[1fr_auto] gap-10 mb-10">
                            <div>
                                <div className="text-[11px] tracking-[0.3em] uppercase text-ink-400/60 mb-2">This certifies that</div>
                                <div className="font-display text-2xl mb-1">{d.buyer_name}</div>
                                <div className="text-sm text-ink-400/70 mb-8">{d.buyer_company || "—"}</div>

                                <div className="text-[11px] tracking-[0.3em] uppercase text-ink-400/60 mb-2">has acquired the full copyright of</div>
                                <div className="font-display text-3xl mb-1">{d.film_title}</div>
                                <div className="text-sm text-ink-400/70">{d.film_country} · {d.film_language} · by {d.creator_name}</div>
                            </div>
                            <div className="flex flex-col items-center">
                                <div
                                    dangerouslySetInnerHTML={{ __html: d.qr_svg.replace("<svg", "<svg width=\"160\" height=\"160\"") }}
                                    className="[&_svg]:w-40 [&_svg]:h-40"
                                    data-testid="credential-qr"
                                />
                                <div className="text-[10px] tracking-widest uppercase text-ink-400/50 mt-2">Scan to verify</div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 mb-10 pt-8 border-t border-ink-400/10">
                            <div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-400/60 mb-1">Consideration</div>
                                <div className="font-mono text-lg">${Number(d.price_usd || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-400/60 mb-1">Issued</div>
                                <div className="text-sm">{new Date(d.issued_at).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-400/60 mb-1">Jurisdiction</div>
                                <div className="text-sm">Indian Copyright Act, 1957 · § 18–19</div>
                            </div>
                        </div>

                        <div className="mb-8 text-sm text-ink-400/80 leading-relaxed">
                            The Copyright of the cinematograph work identified above has been assigned on a worldwide,
                            exclusive, perpetual and irrevocable basis to the Buyer. Chain-of-title has been verified,
                            escrow has been released, and the transfer is recorded on the Silver Screen Xchange public ledger.
                        </div>

                        <div className="pt-6 border-t border-ink-400/10">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-400/60 mb-2 flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5" /> Ledger hash · Verifiable at{" "}
                                <span className="normal-case tracking-normal">{d.verify_url}</span>
                            </div>
                            <div className="font-mono text-[11px] md:text-xs break-all text-ink-400/80" data-testid="credential-hash">{d.chain_hash}</div>
                        </div>
                    </div>
                </div>
            </section>

            <style>{`@media print {
                body { background: white; }
                #credential-sheet { border: none; box-shadow: none; }
            }`}</style>
        </div>
    );
}
